import { useState, useEffect, useRef, useCallback } from 'react';

const DISMISS_KEY = 'pwa-install-dismissed';

/** PWA уже открыто через иконку на рабочем столе, а не в браузере. */
export function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true
  );
}

function isIosDevice() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
}

function isMobile() {
  return /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);
}

/**
 * Управление предложением установить PWA: системный prompt (Android/Chrome)
 * и ручная инструкция для iOS.
 */
export default function usePwaInstall() {
  const [visible, setVisible] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [hasNativePrompt, setHasNativePrompt] = useState(false);
  const deferredRef = useRef(null);

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISS_KEY) === '1') return;
    if (!isMobile()) return;

    const ios = isIosDevice();
    setIsIos(ios);

    if (ios) {
      setVisible(true);
      return undefined;
    }

    const onPrompt = (e) => {
      e.preventDefault();
      deferredRef.current = e;
      setHasNativePrompt(true);
      setVisible(true);
    };

    window.addEventListener('beforeinstallprompt', onPrompt);

    // Chrome не всегда шлёт prompt сразу — показываем ручную подсказку на мобильном
    const timer = setTimeout(() => {
      setVisible((prev) => prev || !deferredRef.current);
    }, 1500);

    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      clearTimeout(timer);
    };
  }, []);

  const install = useCallback(async () => {
    const prompt = deferredRef.current;
    if (!prompt) return false;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === 'accepted') {
      deferredRef.current = null;
      setVisible(false);
      setHasNativePrompt(false);
    }
    return outcome === 'accepted';
  }, []);

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, '1');
    setVisible(false);
  }, []);

  return { visible, isIos, hasNativePrompt, install, dismiss };
}
